import React, { useCallback, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useIdleTimer } from '../hooks/useIdleTimer';
import SessionTimeoutWarning from './SessionTimeoutWarning';
import { SESSION_CONFIG, SESSION_MESSAGES } from '../config/sessionConfig';

interface SessionManagerProps {
  children: React.ReactNode;
}

const SessionManager: React.FC<SessionManagerProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);

  // Handler for when user becomes idle
  const handleIdle = useCallback(async () => {
    console.log('[Session] User idle, logging out...');
    setShowWarning(false);

    // Clean up session storage
    try {
      localStorage.removeItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    } catch {
      // Ignore storage errors
    }

    // Perform logout with idle timeout type
    await logout('IDLE_TIMEOUT_LOGOUT');
  }, [logout]);

  // Handler for when warning should be shown
  const handleWarning = useCallback(() => {
    console.log('[Session] Showing timeout warning');
    setShowWarning(true);
  }, []);

  // Use idle timer hook - only enabled when user is logged in
  const { isWarning, remainingTime, resetTimer } = useIdleTimer({
    onIdle: handleIdle,
    onWarning: handleWarning,
    enabled: !!user,
  });

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    console.log('[Session] User chose to stay logged in');
    setShowWarning(false);
    resetTimer();

    // Notify other tabs via BroadcastChannel
    try {
      const channel = new BroadcastChannel(SESSION_CONFIG.BROADCAST_CHANNEL_NAME);
      channel.postMessage({
        type: SESSION_MESSAGES.EXTEND,
        timestamp: Date.now(),
      });
      channel.close();
    } catch {
      // BroadcastChannel not supported, update localStorage
      try {
        localStorage.setItem(SESSION_CONFIG.LAST_ACTIVITY_KEY, Date.now().toString());
      } catch {
        // Ignore storage errors
      }
    }
  }, [resetTimer]);

  // Handle manual logout from warning modal
  const handleManualLogout = useCallback(async () => {
    console.log('[Session] User chose to logout from warning modal');
    setShowWarning(false);

    // Clean up
    try {
      localStorage.removeItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    } catch {
      // Ignore storage errors
    }

    // Perform logout with user logout type (manual logout from warning)
    await logout('USER_LOGOUT');
  }, [logout]);

  // Sync warning state with hook
  useEffect(() => {
    if (!isWarning && showWarning) {
      // Warning was dismissed elsewhere (e.g., activity detected)
      setShowWarning(false);
    }
  }, [isWarning, showWarning]);

  return (
    <>
      {children}

      {/* Session timeout warning modal */}
      {user && (
        <SessionTimeoutWarning
          isOpen={showWarning}
          remainingTime={remainingTime}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={handleManualLogout}
        />
      )}
    </>
  );
};

export default SessionManager;
